import { faPlus, faListUl, faTrashCan, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { Bars } from 'react-loader-spinner';

import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import OrdersTable, {
  getOrdersTableColumns,
  OrderCablesPanel
} from '../../components/tables/OrdersTable';
import OrdersTopSection from './OrdersTopSection';
import { useAppContext } from '../../providers/AppProvider';

// ✅ TanStack Query (react-query)

import { useOrders, useArchivedOrders } from '../../features/orders/useOrders.js';
import CompletedOrdersPanel from '../../components/sales-management/CompletedOrdersPanel';

const Orders = () => {
  const {
    config: { theme }
  } = useAppContext();

  // Active / Deleted, the same two pills the project list has. A deleted order
  // is archived rather than dropped, so it belongs in the same table with the
  // same columns — a separate page made it look like a different kind of thing.
  const [view, setView] = useState('active'); // 'active' | 'deleted'

  const {
    data: liveOrders = [],
    isLoading,
    isFetching,
    isError,
    error
  } = useOrders();

  const { data: archivedData, isLoading: archLoading } = useArchivedOrders(200);

  const orders = useMemo(() => {
    if (view !== 'deleted') return liveOrders;
    // The archive keeps the order as it was, plus who removed it and why.
    return (archivedData?.orders || []).map(o => ({
      ...o,
      _deleted: true,
      _archivedAt: o._archivedAt || null,
      _archivedBy: o._archivedBy || null,
      _archivedReason: o._archivedReason || null
    }));
  }, [view, liveOrders, archivedData]);

  // Which orders have their cable list open. Keyed by orderId rather than by
  // row index, so paging or re-sorting cannot leave the panel under a
  // different order.
  const [expanded, setExpanded] = useState({});
  const toggleCables = id =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const columns = useMemo(
    () => getOrdersTableColumns({ expanded, onToggle: toggleCables }),
    [expanded]
  );

  const table = useAdvanceTable({
    data: orders,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true
  });

  console.log('ORDERS?  ', orders);

  if ((view === 'deleted' ? archLoading : isLoading) && !orders.length) {
    return (
      <Bars
        height="80"
        width="80"
        radius="9"
        color={theme === 'dark' ? 'white' : '#3874ff'}
        ariaLabel="three-dots-loading"
        wrapperStyle={styles.spinnerContainer}
      />
    );
  }

  return (
    <div>
      <div className="mb-9">
        <AdvanceTableProvider {...table}>
          <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
            <h2 className="mb-0">
              <span className="me-3">Orders</span>{' '}
              <span className="fw-normal text-body-tertiary">
                ({orders.length})
              </span>
              {isFetching && (
                <span className="ms-2 fs-10 text-body-tertiary">
                  (refreshing...)
                </span>
              )}
            </h2>

            <Link
              className="btn btn-primary px-5"
              to="/apps/sales-management/create-new"
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add new Order
            </Link>
          </div>

          <Nav variant="pills" className="mb-3 gap-2">
            <Nav.Item>
              <Nav.Link
                active={view === 'active'}
                onClick={() => setView('active')}
                style={{ cursor: 'pointer' }}
              >
                <FontAwesomeIcon icon={faListUl} className="me-2" />
                Active
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={view === 'completed'}
                onClick={() => setView('completed')}
                style={{ cursor: 'pointer' }}
              >
                <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                Completed
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={view === 'deleted'}
                onClick={() => setView('deleted')}
                style={{ cursor: 'pointer' }}
              >
                <FontAwesomeIcon icon={faTrashCan} className="me-2" />
                Deleted
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {view === 'completed' ? (
            <CompletedOrdersPanel />
          ) : (
            <>
              {view === 'active' && <OrdersTopSection activeView="list" />}
              <OrdersTable
                renderSubRow={row =>
                  expanded[String(row.original.orderId)] ? (
                    <OrderCablesPanel order={row.original} />
                  ) : null
                }
              />
            </>
          )}
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

const styles = {
  spinnerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }
};

export default Orders;
